import dayjs from '~/timeInit';

import * as libRegions from '@luxuryescapes/lib-regions';

import { brandsByValue } from '~/consts/brands';

import { TourOffer } from '~/types/tours/tourOffer';

const ONLINE_PURCHASE = 'online_purchase';

/** A region a product sells in, under one brand. */
export interface Market {
  region: string;
  brand: string;
}

const marketKey = (market: Market) => `${market.region}|${market.brand}`;

function uniqueMarkets(markets: Array<Market>): Array<Market> {
  return [...new Map(markets.map((market) => [marketKey(market), market])).values()];
}

/** Where a flash hotel can be bought right now, from its purchase schedules. */
export function hotelOnSaleMarkets(offer: App.Offer, now = dayjs()): Array<Market> {
  return uniqueMarkets(
    offer.brand_schedules
      .filter(
        (schedule) => schedule.type === ONLINE_PURCHASE && !now.isBefore(schedule.start) && now.isBefore(schedule.end),
      )
      .map((schedule) => ({ region: schedule.region, brand: schedule.brand })),
  );
}

/** Where a tour is scheduled to sell, from its schedules that haven't ended. */
export function tourScheduledMarkets(offer: TourOffer, now = dayjs()): Array<Market> {
  return uniqueMarkets(
    offer.schedules
      .filter((schedule) => now.isBefore(schedule.endDate))
      .flatMap((schedule) =>
        // svc-tour stores region codes in lower case.
        schedule.regions.flatMap((region) =>
          schedule.whiteLabelBrands.map((brand) => ({ region: region.toUpperCase(), brand })),
        ),
      ),
  );
}

export function sharedMarkets(a: Array<Market>, b: Array<Market>): Array<Market> {
  const keys = new Set(b.map(marketKey));
  return a.filter((market) => keys.has(marketKey(market)));
}

export function marketRegions(markets: Array<Market>): Array<string> {
  return [...new Set(markets.map((market) => market.region))].sort();
}

export function marketBrands(markets: Array<Market>): Array<string> {
  return [...new Set(markets.map((market) => market.brand))].sort();
}

export function hotelOnSaleRegions(offer: App.Offer, now = dayjs()): Array<string> {
  return marketRegions(hotelOnSaleMarkets(offer, now));
}

const REGION_NAMES = new Map(libRegions.getRegions().map((region) => [region.code, region.name]));

export function regionName(code: string): string {
  return REGION_NAMES.get(code) ?? code;
}

export function brandName(code: string): string {
  return brandsByValue[code]?.title ?? code;
}
